import { LoadingButton } from "@mui/lab";
import {
  Box,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import useAxiosInstance from "../../../hooks/useAxiosInstance";
import CustomDialogTitle from "../../../components/Common/customDialogTitle";
import { API_RESULT, API_VERSION } from "../../../enums/common.enum";
import { handleError, successToast, errorToast } from "../../../utils/common";

const UpdateApplicationModal = ({
  updateApplication,
  setUpdateApplication,
  activeApplicationId,
  invoiceUrl,
  accountNumber,
  ifscCode,
  setAccountNumber,
  setIfscCode,
  activeBorrowerId,
  payeeName,
  payableAmount,
  callBack,
}: any) => {
  const privateInstance = useAxiosInstance();
  console.log("Invoice_url", invoiceUrl);
  const [action, setAction] = useState<"approved" | "rejected" | "">("");
  const [rejectReason, setRejectReason] = useState("");
  const [loading, setLoading] = useState(false);

  const submitDecision = async (decision: "approved" | "rejected") => {
    if (decision === "rejected" && !rejectReason.trim()) {
      errorToast("Please enter rejection reason");
      return;
    }

    setAction(decision);
    setLoading(true);

    const payload = {
      borrower_id: activeBorrowerId,
      application_id: activeApplicationId,
      status: decision,
      reject_reason: decision === "rejected" ? rejectReason : null,
    };

    try {
      const res = await privateInstance.post(
        `${API_VERSION.V3}/borrowers/applications`,
        payload,
      );

      if (res?.data?.status === API_RESULT.SUCCESS) {
        successToast("Invoice updated successfully");
        setUpdateApplication(false);
        callBack?.();
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={updateApplication}
      onClose={() => setUpdateApplication(false)}
      fullWidth
      maxWidth="md"
    >
      <CustomDialogTitle
        id="customized-dialog-title"
        onClose={() => setUpdateApplication(false)}
      >
        <Typography variant="body2">Invoice Review</Typography>
      </CustomDialogTitle>

      <DialogContent>
        <Typography mb={1}>
          <strong>₹{payableAmount}/-</strong> to be disbursed to{" "}
          <strong>{payeeName}</strong>. Please verify the details, amount and
          account details against the uploaded invoice."
        </Typography>

        <Box mb={3}>
          <Typography variant="h6" mb={1}>
            Invoice Preview
          </Typography>

          {invoiceUrl ? (
            invoiceUrl.endsWith(".pdf") ? (
              <Box
                sx={{
                  mt: 1,
                  pt: 2,
                  px: 2,
                  pb: 2,
                  border: "1px solid #d6d9de",
                  borderRadius: 2,
                  position: "relative",
                  backgroundColor: "#fafafa",
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <PictureAsPdfIcon color="error" />
                  <Box flex={1}>
                    <Typography fontWeight={500}>Invoice</Typography>
                    <Typography variant="body2" color="text.secondary">
                      PDF Document
                    </Typography>
                  </Box>
                  <LoadingButton
                    size="small"
                    variant="outlined"
                    onClick={() => window.open(invoiceUrl, "_blank")}
                  >
                    View
                  </LoadingButton>
                </Box>
              </Box>
            ) : (
              <Box
                component="img"
                src={invoiceUrl}
                alt="Invoice"
                onClick={() => window.open(invoiceUrl, "_blank")}
                sx={{
                  width: "100%",
                  maxHeight: 450,
                  objectFit: "contain",
                  border: "1px solid #ddd",
                }}
              />
            )
          ) : (
            <Typography color="error">Invoice not available</Typography>
          )}
        </Box>

        {action !== "rejected" && (
          <Box mb={3}>
            <Typography variant="h6" mb={1}>
              Account Details
            </Typography>

            <>
              <Box sx={{ mt: 1.5, display: "flex", flexDirection: "row" }}>
                <TextField
                  fullWidth
                  id="account_no"
                  label="Account Number"
                  size="small"
                  value={accountNumber ? `${accountNumber}` : ""}
                  sx={{ mt: 1.5, mr: { xs: 0, md: 1 } }}
                  disabled
                  onChange={(e) => setAccountNumber(e.target.value)}
                />

                <TextField
                  fullWidth
                  id="ifsc_code"
                  label="IFSC Code"
                  size="small"
                  value={ifscCode || ""}
                  sx={{ mt: 1.5, mr: { xs: 0, md: 1 } }}
                  disabled
                  onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                />
              </Box>
            </>
          </Box>
        )}

        {action === "" && (
          <Box display="flex" justifyContent="center" gap={2} mt={3}>
            <LoadingButton
              color="success"
              variant="contained"
              loading={loading}
              disabled={loading}
              onClick={() => submitDecision("approved")}
            >
              Approve
            </LoadingButton>

            <LoadingButton
              color="error"
              variant="contained"
              onClick={() => setAction("rejected")}
            >
              Reject
            </LoadingButton>
          </Box>
        )}

        {action === "rejected" && (
          <>
            <TextField
              label="Rejection Reason"
              fullWidth
              multiline
              rows={3}
              sx={{ mt: 3 }}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />

            <Box textAlign="center" mt={3}>
              <LoadingButton
                variant="contained"
                color="primary"
                loading={loading}
                disabled={loading}
                onClick={() => submitDecision("rejected")}
              >
                Submit
              </LoadingButton>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UpdateApplicationModal;
